'use client'

import { FC, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import Link from 'next/link'

import { METHOD_2FA_ENUM } from '@/common/constants'
import { Button, Checkbox, Input, ModalBase } from '@/components/commons'
import {
  EyeIcon,
  EyeNoIcon,
  OnlypayLogoCompleteNewIcon,
} from '@/components/icons'
import { useAuthProvider } from '@/contexts/auth/AuthProvider'
import { useBasicInfoService } from '@/services/auth/basicInfoService/basicInfoService'
import {
  ICreate2FARequest,
  useCreate2FAService,
} from '@/services/auth/create2FAService/create2FAService'
import { routerPaths } from '@/utils/constants/routes'
import { yupResolver } from '@hookform/resolvers/yup'

import { CodeForm, ContactForm } from '../'
import ModalInformation from '../../../ModalInformation'
import { FormProps } from '../../types'
import { FORM_CASE_ENUM } from '../../utils/constants'
import { schema } from '../../utils/validations'
import { ILegacySignInFormProps } from './types'

const LegacySignInForm: FC<ILegacySignInFormProps> = ({
  isRememberMe,
  savedLoginUser,
}) => {
  const {
    login2FA,
    customMessageError,
    isOpenModal,
    setIsOpenModal,
    modalVariant,
    setCustomMessageError,
    setModalVariant,
  } = useAuthProvider()

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingLogin, setIsLoadingLogin] = useState(false)
  const [toggleEye, setToggleEye] = useState(false)
  const [formCase, setFormCase] = useState<FORM_CASE_ENUM>(
    FORM_CASE_ENUM.CONTACT_FORM,
  )
  const [showModalMethod, setShowModalMethod] = useState<boolean>(false)
  const [currentMethod, setCurrentMethod] = useState<METHOD_2FA_ENUM>(
    METHOD_2FA_ENUM.EMAIL,
  )

  const { mutateAsync: basicInfoMutateAsync, data: dataBasicInfo } =
    useBasicInfoService()
  const { mutateAsync: create2FAMutateAsync, isPending: create2FAIsPending } =
    useCreate2FAService()

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormProps>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      userName: savedLoginUser ?? '',
      pwd: '',
      rememberMe: isRememberMe,
    },
  })

  /**
   * Busca as informações de contato para enviar o código 2FA
   */
  async function handleGetBasicInfo() {
    const { pwd, userName } = getValues()
    setIsLoading(true)

    await basicInfoMutateAsync({
      pwd,
      userIp: '000.000.000.000',
      userName,
    })
      .then(() => {
        setShowModalMethod(true)
      })
      .catch((error) => {
        setCustomMessageError(
          error?.response?.data ??
            'Erro ao buscar os contatos para envio do código de autenticação.',
        )
        setModalVariant('customMessage')
        setIsOpenModal(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  /**
   * Submete o formulário
   */
  const onSubmit: SubmitHandler<FormProps> = async () => {
    await handleGetBasicInfo()
    setFormCase(FORM_CASE_ENUM.CONTACT_FORM)
  }

  /**
   * Envia o código de acesso 2FA para o contato escolhido
   * Atualiza o state formCase para FORM_CASE_ENUM.CODE_FORM
   */
  async function handleSendCode(method?: ICreate2FARequest['method2FA']) {
    const { pwd, userName } = getValues()
    const methodSerialized = {
      method: currentMethod,
    }

    if (method) {
      methodSerialized.method = method
      setCurrentMethod(method)
    }

    await create2FAMutateAsync({
      method2FA: methodSerialized.method,
      pwd,
      userIp: '000.000.000.000',
      userName,
    })
      .then(() => {
        setFormCase(FORM_CASE_ENUM.CODE_FORM)
      })
      .catch((error) => {
        setCustomMessageError(
          error?.response?.data ?? 'Erro ao enviar o código de autenticação.',
        )
        setModalVariant('customMessage')
        setIsOpenModal(true)
      })
  }

  /**
   * Valida o código recebido e faz o login
   */
  async function handleConfirmationCode(code: string) {
    const { pwd, userName, rememberMe } = getValues()
    setIsLoadingLogin(true)

    await login2FA({
      pwd,
      userName,
      rememberMe,
      code,
    }).finally(() => {
      setIsLoading(false)
    })
  }

  return (
    <>
      <div>
        <OnlypayLogoCompleteNewIcon className="self-center md:self-start" />
      </div>

      <form
        className="flex flex-col w-full bg-white gap-8"
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
      >
        <h1 className="prose-headline-h6 text-primary-900 text-center md:text-start">
          Informe os dados para acessar
        </h1>

        <Input
          {...register('userName')}
          autoCapitalize="none"
          autoComplete="off"
          label="Usuário"
          mask="999.999.999-99"
          placeholder="Digite seu CPF ou CNPJ"
          onlyNums
          wFull
          helperText={errors.userName?.message}
        />

        <Input
          {...register('pwd')}
          autoCapitalize="none"
          autoComplete="off"
          label="Senha"
          placeholder="*****"
          type={toggleEye ? 'text' : 'password'}
          iconRight={
            toggleEye ? (
              <EyeIcon onClick={() => setToggleEye(!toggleEye)} />
            ) : (
              <EyeNoIcon onClick={() => setToggleEye(!toggleEye)} />
            )
          }
          classNameIconRight="cursor-pointer"
          wFull
          helperText={errors.pwd?.message}
        />

        <Button
          text="Entrar"
          type="submit"
          className="w-full"
          onClick={(e) => {
            if (isLoading) {
              e.preventDefault()
            }
          }}
          isLoading={isLoading}
        />

        <div className="flex w-full items-center justify-between gap-8">
          <Checkbox {...register('rememberMe')} label="Lembrar de mim?" />
          <Link href={routerPaths.public.forgotPassword.default}>
            <p className="prose-body-small text-black hover:underline">
              Esqueceu a senha?
            </p>
          </Link>
        </div>
      </form>

      <div className="flex w-full items-center justify-center gap-8">
        <p className="prose-body-small text-primary-900 text-center md:text-start">
          Ainda não tem uma conta? Fale com o seu administrador.
        </p>
      </div>

      {/* Modais */}
      {showModalMethod && (
        <ModalBase
          className="w-full max-w-[464px]"
          isOpenModal={showModalMethod}
        >
          <ModalBase.Body className="justify-end">
            {formCase === FORM_CASE_ENUM.CONTACT_FORM && dataBasicInfo && (
              <ContactForm
                sendButtonLoading={create2FAIsPending}
                setFormCase={setFormCase}
                contacts={dataBasicInfo}
                currentMethod={currentMethod}
                setIsOpenModal={setShowModalMethod}
                handleSendCode={handleSendCode}
                setCurrentMethod={setCurrentMethod}
              />
            )}
            {formCase !== FORM_CASE_ENUM.CONTACT_FORM && dataBasicInfo && (
              <CodeForm
                sendButtonLoading={isLoadingLogin}
                setFormCase={setFormCase}
                contacts={dataBasicInfo}
                currentMethod={currentMethod}
                setIsOpenModal={setShowModalMethod}
                handleConfirmationCode={handleConfirmationCode}
                onResend={handleSendCode}
                haveACode={formCase === FORM_CASE_ENUM.HAS_A_CODE}
              />
            )}
          </ModalBase.Body>
        </ModalBase>
      )}

      {isOpenModal && (
        <ModalInformation
          variant={modalVariant}
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          customMessage={customMessageError}
        />
      )}
    </>
  )
}

export default LegacySignInForm
